/**
 * LEAD-CONTACT-FLOW-V1 -- the NEXT-card contact workflow's PURE view model
 * (no I/O, no React). The server is the timer authority: it issues the
 * grant (issuedAt/expiresAt from the org-config horizon
 * lead_reveal.grant_seconds, seeded 960); everything here only RENDERS
 * that truth -- countdown math, state transitions, and controlled copy.
 *
 * Testability follows the repo idiom (lead-disposition-view.ts): the pure
 * model is the pinned surface; LeadContactWorkflow.tsx renders from it.
 */

import type { ContactWorkflowStartPayload } from '@/dashboard/api';
import {
  DENIED_COPY,
  REVEAL_DISABLED_COPY,
  REVEAL_FAILED_COPY,
} from '@/dashboard/owner/LeadContactReveal';

/** The charter's two channels (members of the EXISTING LeadContactChannel
 *  vocabulary server-side; never a new enum). */
export type ContactChannelChoice = 'phone' | 'whatsapp';

export const CHOOSER_GROUP_LABEL = 'Contact this lead';
export const CHOOSER_PHONE_LABEL = 'Phone';
export const CHOOSER_WHATSAPP_LABEL = 'WhatsApp';
export const ADVANCE_HEADING = 'How did the contact go?';
export const CLOSE_AND_REMASK_LABEL = 'Close and re-mask';
export const REMASKED_NOTICE = 'Number re-masked.';
export const REVEAL_AGAIN_LABEL = 'Reveal again';
export const COUNTDOWN_PREFIX = 'Re-masks in';

/** THE HONEST SEAM (design-language SEAM law): the dial/send integration
 *  is NOT built -- these render as TEXT, never a tappable control. The
 *  actual integration (Office24x7 / Interakt) lands as its own package. */
export const PHONE_SEAM_LINE =
  'Dial from your desk or mobile phone - click-to-dial (Office24x7) is coming.';
export const WHATSAPP_SEAM_LINE =
  'Open WhatsApp on your device to message - in-app send (Interakt) is coming.';

/** S-4 honesty for the DESIGNED no_value outcomes (reasons from
 *  lib/lead-contact-reveal.ts LeadContactRevealNoValueReason). */
export const NO_VALUE_REASON_COPY: Readonly<Record<string, string>> = {
  no_linked_phone: 'No linked phone yet',
  no_linked_contact: 'No matching contact record yet',
  no_stored_value: 'No phone stored for this contact',
};
export const NO_VALUE_NEXT_LINE =
  'Add a note from the record menu, or close the lead if it cannot be reached.';

export interface ContactWorkflowGrantView {
  readonly grantSeconds: number;
  readonly issuedAtIso: string;
  readonly expiresAtIso: string;
}

/**
 * The workflow's UI state machine. 'active' with value=null is the
 * RE-MASKED state (grant expired or manually closed): the plaintext is
 * DROPPED from state -- never retained past the mask -- while the advance
 * step (disposition) stays available.
 */
export type ContactWorkflowUiState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'starting'; readonly channel: ContactChannelChoice }
  | {
      readonly kind: 'active';
      readonly channel: ContactChannelChoice;
      readonly purpose: string;
      readonly value: string | null;
      readonly noValueReason: string | null;
      readonly grant: ContactWorkflowGrantView | null;
    }
  | { readonly kind: 'denied'; readonly message: string }
  | { readonly kind: 'disabled'; readonly message: string }
  | { readonly kind: 'failed'; readonly message: string };

export const CONTACT_WORKFLOW_IDLE: ContactWorkflowUiState = { kind: 'idle' };

export function contactStateStarting(
  channel: ContactChannelChoice,
): ContactWorkflowUiState {
  return { kind: 'starting', channel };
}

/** The state after the ONE server action resolved. */
export function contactStateAfterStart(
  payload: ContactWorkflowStartPayload,
): ContactWorkflowUiState {
  return {
    kind: 'active',
    channel: payload.channel,
    purpose: payload.purpose,
    value: payload.reveal.outcome === 'revealed' ? payload.reveal.value : null,
    noValueReason:
      payload.reveal.outcome === 'no_value' ? payload.reveal.reason : null,
    grant: payload.grant,
  };
}

/** 401 -> denied (scope), 503 -> disabled (flag dark), else failed with a
 *  retry -- the same mapping the sibling reveal control ships. */
export function contactStateAfterError(
  status: number | null,
): ContactWorkflowUiState {
  if (status === 401) return { kind: 'denied', message: DENIED_COPY };
  if (status === 503) return { kind: 'disabled', message: REVEAL_DISABLED_COPY };
  return { kind: 'failed', message: REVEAL_FAILED_COPY };
}

/** Re-mask: DROP the plaintext, keep the workflow (advance step) alive.
 *  Idempotent; a non-active state passes through unchanged. Re-masking is
 *  a DISPLAY act -- the reveal is already audited; no second row, no
 *  second call. */
export function contactStateMasked(
  state: ContactWorkflowUiState,
): ContactWorkflowUiState {
  if (state.kind !== 'active' || state.value === null) return state;
  return { ...state, value: null };
}

/** Whether the band's actions row should hide + the composer strip show
 *  (the NextActionEdge `invoked` contract). */
export function contactWorkflowInvoked(state: ContactWorkflowUiState): boolean {
  return state.kind !== 'idle';
}

/** Seconds left on the server-issued grant, floored at zero -- the client
 *  renders this; it never extends what the server issued. */
export function grantRemainingSeconds(
  expiresAtIso: string,
  nowMs: number,
): number {
  const expiresMs = Date.parse(expiresAtIso);
  if (!Number.isFinite(expiresMs)) return 0;
  return Math.max(0, Math.floor((expiresMs - nowMs) / 1000));
}

/** m:ss (e.g. 960 -> "16:00"; 65 -> "1:05"; 0 -> "0:00"). */
export function formatGrantCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** The seam line for the chosen channel (TEXT only -- SEAM law). */
export function channelSeamLine(channel: ContactChannelChoice): string {
  return channel === 'whatsapp' ? WHATSAPP_SEAM_LINE : PHONE_SEAM_LINE;
}
